/**
 * Step 4: AI Analysis
 * Displays AI analysis progress and results
 */

import { useState } from 'react'
import {
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Progress,
  Tag,
  Badge,
  Input,
  Segmented,
  Typography,
  Spin,
  Tooltip,
  Button,
  Popover,
  Empty,
  Collapse,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  LoadingOutlined,
  ExperimentOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  CodeOutlined,
  CopyOutlined,
  CheckOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { DomainWithAnalysis, AIConfig } from '../types'

const { Text, Title } = Typography

/**
 * Generate AI prompt preview based on config
 * This mirrors the logic in thematic_analyzer.py
 */
function generatePromptPreview(config: AIConfig): string {
  const keywordsStr = config.focus_keywords?.slice(0, 20).join(', ') || 'не заданы'
  const audiencesStr = config.target_audiences?.join(', ') || 'любая'
  const nicheStr = config.niche || config.project_name || 'не указана'

  // Build context sections
  const contextSections: string[] = []

  if (config.queries && config.queries.length > 0) {
    const queriesSample = config.queries.slice(0, 15).join(', ')
    const extra = config.queries.length > 15 ? ` (+${config.queries.length - 15} ещё)` : ''
    contextSections.push(`ПОИСКОВЫЕ ЗАПРОСЫ (по которым ищут клиентов):\n${queriesSample}${extra}`)
  }

  if (config.regions && config.regions.length > 0) {
    const regionsSample = config.regions.slice(0, 10).join(', ')
    const extra = config.regions.length > 10 ? ` (+${config.regions.length - 10} ещё)` : ''
    contextSections.push(`ЦЕЛЕВЫЕ РЕГИОНЫ:\n${regionsSample}${extra}`)
  }

  if (config.crm_script) {
    contextSections.push(`СКРИПТ CRM (что говорить клиенту):\n${config.crm_script}`)
  }

  if (config.vdl_tz) {
    contextSections.push(`ТЕХНИЧЕСКОЕ ЗАДАНИЕ VDL (требования к сайту):\n${config.vdl_tz}`)
  }

  const contextStr = contextSections.join('\n\n')

  return `Проанализируй сайт и верни ТОЛЬКО JSON без дополнительного текста.

${config.ai_instructions || ''}

=== КОНТЕКСТ ПРОЕКТА ===
НИША/ТЕМАТИКА: ${nicheStr}
КЛЮЧЕВЫЕ СЛОВА НИШИ: ${keywordsStr}
ЦЕЛЕВАЯ АУДИТОРИЯ: ${audiencesStr}

${contextStr}
========================

ВАЖНО! Ты ищешь КОНКУРЕНТОВ для ниши "${nicheStr}".
Сайт релевантен, ТОЛЬКО если он:
1. Предоставляет услуги/товары по указанным поисковым запросам
2. Работает в указанных регионах (или по всей России)
3. Является КОММЕРЧЕСКИМ сайтом (не агрегатор, не соцсеть, не справочник)

САЙТ ДЛЯ АНАЛИЗА:
URL: {url}
Заголовок: {title}
Контент (первые 5000 символов):
{content}

Верни JSON в формате:
{
    "thematic_relevance": <число от 0 до 10>,
    "target_audience": "<одна из: ${audiencesStr}>",
    "niche_detected": "<название ниши сайта>",
    "company_quality_score": <число от 0 до 10>,
    "conclusion": "<краткий вывод на русском, 1-2 предложения, объясни ПОЧЕМУ сайт подходит или не подходит>"
}

КРИТЕРИИ ОЦЕНКИ thematic_relevance:
- 0-2: НЕ относится к нише (соцсети, агрегаторы, справочники, другая тематика)
- 3-4: Косвенно связан (смежная тематика, не точное соответствие запросам)
- 5-6: Частично подходит (правильная ниша, но не все услуги/регионы)
- 7-8: Хорошо подходит (соответствует нише и запросам)
- 9-10: Идеально подходит (прямой конкурент в нужном регионе)`
}

/**
 * Component for displaying AI prompt preview with copy functionality
 */
function PromptPreviewBlock({ config }: { config: AIConfig }) {
  const [copied, setCopied] = useState(false)
  const promptText = generatePromptPreview(config)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(promptText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Check if we have meaningful context
  const hasQueries = config.queries && config.queries.length > 0
  const hasRegions = config.regions && config.regions.length > 0
  const hasNiche = config.niche || config.project_name

  return (
    <Card
      size="small"
      style={{
        textAlign: 'left',
        marginBottom: 16,
        maxWidth: 800,
        marginLeft: 'auto',
        marginRight: 'auto',
        border: '1px solid #d9d9d9',
      }}
    >
      <Collapse
        ghost
        items={[
          {
            key: '1',
            label: (
              <Space>
                <CodeOutlined style={{ color: '#722ed1' }} />
                <Text strong>Промпт для AI анализа</Text>
                {hasQueries && <Tag color="blue">{config.queries!.length} запросов</Tag>}
                {hasRegions && <Tag color="green">{config.regions!.length} регионов</Tag>}
                {hasNiche && <Tag color="purple">{config.niche || config.project_name}</Tag>}
              </Space>
            ),
            children: (
              <div>
                {/* Context summary */}
                <div style={{ marginBottom: 12 }}>
                  <Row gutter={[8, 8]}>
                    <Col span={24}>
                      <Alert
                        type={hasQueries && hasRegions ? 'success' : 'warning'}
                        message={
                          hasQueries && hasRegions
                            ? 'Контекст проекта загружен'
                            : 'Контекст проекта неполный'
                        }
                        description={
                          <Space direction="vertical" size={4}>
                            <Text>
                              <strong>Ниша:</strong> {config.niche || config.project_name || <Text type="warning">не указана</Text>}
                            </Text>
                            <Text>
                              <strong>Запросы:</strong> {hasQueries ? `${config.queries!.length} шт.` : <Text type="warning">не загружены</Text>}
                            </Text>
                            <Text>
                              <strong>Регионы:</strong> {hasRegions ? `${config.regions!.length} шт.` : <Text type="warning">не загружены</Text>}
                            </Text>
                          </Space>
                        }
                        showIcon
                        style={{ marginBottom: 12 }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* Prompt text */}
                <div style={{ position: 'relative' }}>
                  <Button
                    size="small"
                    icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopy}
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                    }}
                  >
                    {copied ? 'Скопировано!' : 'Копировать'}
                  </Button>
                  <pre
                    style={{
                      background: '#1e1e1e',
                      color: '#d4d4d4',
                      padding: '16px',
                      paddingTop: '40px',
                      borderRadius: 8,
                      fontSize: 12,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 400,
                      overflow: 'auto',
                      margin: 0,
                    }}
                  >
                    {promptText}
                  </pre>
                </div>

                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 11 }}>
                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                  Этот промпт будет отправлен в Gemini AI для каждого источника. Переменные {'{url}'}, {'{title}'}, {'{content}'} будут заменены данными сайта.
                </Text>
              </div>
            ),
          },
        ]}
      />
    </Card>
  )
}

interface QueueInfo {
  position: number
  ahead_batches: number
  urls_ahead: number
  estimated_wait_minutes: number
  running_batch: { batch_id: number; total: number; processed: number } | null
}

interface AIAnalysisStepProps {
  domains: DomainWithAnalysis[]
  aiConfig: AIConfig | null
  loadingAIConfig: boolean
  analyzing: boolean
  batchProgress: { processed: number; total: number } | null
  queueInfo?: QueueInfo | null
  aiFilter: 'all' | 'approved' | 'rejected' | 'uncertain'
  onAiFilterChange: (filter: 'all' | 'approved' | 'rejected' | 'uncertain') => void
  aiSearchText: string
  onAiSearchTextChange: (text: string) => void
  onStartAnalysis: () => void
  currentBatchConfigVersion?: number | null
  currentProjectConfigVersion?: number | null
  onReanalyze?: () => Promise<void> | void
  reanalyzing?: boolean
}

export default function AIAnalysisStep({
  domains,
  aiConfig,
  loadingAIConfig,
  analyzing,
  batchProgress,
  queueInfo,
  aiFilter,
  onAiFilterChange,
  aiSearchText,
  onAiSearchTextChange,
  onStartAnalysis,
  currentBatchConfigVersion = null,
  currentProjectConfigVersion = null,
  onReanalyze,
  reanalyzing = false,
}: AIAnalysisStepProps) {
  // Count domains that have been analyzed (ai_score is a number, not null/undefined)
  const domainsAnalyzedCount = domains.filter((d) => typeof d.ai_score === 'number').length

  // Use batchProgress if available (for large batches where domains aren't updated during polling)
  // Otherwise fall back to counting from domains array
  const analyzedCount = batchProgress?.processed ?? domainsAnalyzedCount
  const totalCount = batchProgress?.total ?? domains.length
  const pendingCount = totalCount - analyzedCount
  const progressPercent = totalCount > 0 ? (analyzedCount / totalCount) * 100 : 0

  // Calculate statistics
  const approvedCount = domains.filter((d) => d.ai_recommendation === 'approved').length
  const rejectedCount = domains.filter((d) => d.ai_recommendation === 'rejected').length
  const uncertainCount = domains.filter((d) => d.ai_recommendation === 'uncertain').length

  // Filter and search logic
  const filteredDomains = domains.filter((d) => {
    if (aiFilter !== 'all') {
      if (!d.ai_recommendation) return false
      if (d.ai_recommendation !== aiFilter) return false
    }

    if (aiSearchText) {
      const searchLower = aiSearchText.toLowerCase()
      const hostMatch = d.host.toLowerCase().includes(searchLower)
      const urlMatch = d.full_url?.toLowerCase().includes(searchLower) || false
      if (!hostMatch && !urlMatch) return false
    }

    return true
  })

  // Table columns
  const aiAnalysisColumns: ColumnsType<DomainWithAnalysis> = [
    {
      title: 'Источник',
      dataIndex: 'host',
      key: 'host',
      width: '30%',
      ellipsis: true,
      render: (_, record) => {
        const isPage = record.full_url && (() => {
          try {
            const url = new URL(record.full_url)
            return url.pathname !== '/' && url.pathname !== ''
          } catch { return false }
        })()
        const displayText = isPage ? record.full_url : record.host
        const url = record.full_url || (record.host.startsWith('http') ? record.host : `https://${record.host}`)
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', fontWeight: 600, fontSize: 13 }}>
            {displayText}
          </a>
        )
      },
    },
    {
      title: 'Статус',
      key: 'recommendation',
      width: 140,
      align: 'center',
      render: (_, record) => {
        if (record.analyzing) {
          return <Tag color="processing" icon={<LoadingOutlined />}>Анализируется</Tag>
        }

        if (!record.ai_recommendation) {
          return <Tag color="default">Ожидает</Tag>
        }

        const config = {
          approved: { icon: <CheckCircleOutlined />, color: 'success', text: 'Одобрен' },
          rejected: { icon: <CloseCircleOutlined />, color: 'error', text: 'Отклонен' },
          uncertain: { icon: <QuestionCircleOutlined />, color: 'warning', text: 'Требует проверки' },
        }[record.ai_recommendation]

        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        )
      },
    },
    {
      title: 'Оценка AI',
      key: 'score',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.ai_score || 0) - (b.ai_score || 0),
      render: (_, record) => {
        if (record.ai_score === undefined) return <Text type="secondary">—</Text>

        const getColor = () => {
          if (record.ai_score! >= 70) return '#52c41a'
          if (record.ai_score! >= 40) return '#faad14'
          return '#ff4d4f'
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Progress
              type="circle"
              percent={record.ai_score}
              size={48}
              strokeColor={getColor()}
              format={(percent) => <span style={{ fontSize: 12, fontWeight: 600 }}>{percent}</span>}
            />
          </div>
        )
      },
    },
    {
      title: 'Причина',
      key: 'reason',
      ellipsis: true,
      render: (_, record) => {
        const reason = record.ai_reason || 'Причина не указана'
        const truncated = reason.length > 60 ? `${reason.substring(0, 60)}...` : reason

        if (reason.length > 60) {
          return (
            <Popover
              content={
                <div style={{ maxWidth: 400 }}>
                  <Text>{reason}</Text>
                </div>
              }
              title="Полная причина"
              trigger="hover"
            >
              <Text type="secondary" style={{ cursor: 'help' }}>
                {truncated} <InfoCircleOutlined style={{ fontSize: 12, marginLeft: 4 }} />
              </Text>
            </Popover>
          )
        }

        return <Text type="secondary">{reason}</Text>
      },
    },
    {
      title: 'Клики',
      dataIndex: 'total_clicks',
      key: 'clicks',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.total_clicks - b.total_clicks,
      render: (clicks) => (
        <Badge
          count={clicks.toLocaleString()}
          showZero
          style={{ backgroundColor: '#1890ff' }}
        />
      ),
    },
  ]

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <ExperimentOutlined style={{ color: '#722ed1', fontSize: 20 }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>AI Анализ источников</span>
          </Space>
        }
        extra={
          aiConfig && (
            <Space size="small">
              {onReanalyze && analyzedCount > 0 && !analyzing && (
                <Tooltip title="Перезапустить AI-анализ для тех же источников с актуальной версией конфига. Прежние результаты сохраняются для аудита.">
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={reanalyzing}
                    onClick={() => onReanalyze()}
                  >
                    Пересчитать
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Конфигурация AI">
                <Popover
                content={
                  <Space direction="vertical" size="small" style={{ maxWidth: 600, maxHeight: 500, overflowY: 'auto' }}>
                    {aiConfig.project_name && (
                      <div>
                        <Text type="secondary">Проект: </Text>
                        <Text strong>{aiConfig.project_name}</Text>
                      </div>
                    )}
                    {aiConfig.niche && (
                      <div>
                        <Text type="secondary">Ниша: </Text>
                        <Tag color="blue">{aiConfig.niche}</Tag>
                      </div>
                    )}
                    {aiConfig.queries && aiConfig.queries.length > 0 && (
                      <div>
                        <Text type="secondary">Запросов: </Text>
                        <Text>{aiConfig.queries.length}</Text>
                      </div>
                    )}
                    {aiConfig.ai_instructions && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                          Промпт к нейронке:
                        </Text>
                        <div style={{
                          background: '#f5f5f5',
                          padding: '8px 12px',
                          borderRadius: 4,
                          fontSize: 12,
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                          maxHeight: 300,
                          overflowY: 'auto'
                        }}>
                          {aiConfig.ai_instructions}
                        </div>
                      </div>
                    )}
                    {aiConfig.focus_keywords && aiConfig.focus_keywords.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                          Фокус-слова:
                        </Text>
                        <Space wrap size={4}>
                          {aiConfig.focus_keywords.slice(0, 10).map((kw, idx) => (
                            <Tag key={idx} color="green" style={{ fontSize: 11 }}>{kw}</Tag>
                          ))}
                          {aiConfig.focus_keywords.length > 10 && (
                            <Tag style={{ fontSize: 11 }}>+{aiConfig.focus_keywords.length - 10}</Tag>
                          )}
                        </Space>
                      </div>
                    )}
                    {aiConfig.business_keywords && aiConfig.business_keywords.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                          Бизнес-ключи:
                        </Text>
                        <Space wrap size={4}>
                          {aiConfig.business_keywords.slice(0, 10).map((kw, idx) => (
                            <Tag key={idx} color="blue" style={{ fontSize: 11 }}>{kw}</Tag>
                          ))}
                          {aiConfig.business_keywords.length > 10 && (
                            <Tag style={{ fontSize: 11 }}>+{aiConfig.business_keywords.length - 10}</Tag>
                          )}
                        </Space>
                      </div>
                    )}
                    {aiConfig.exclude_keywords && aiConfig.exclude_keywords.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                          Слова исключения:
                        </Text>
                        <Space wrap size={4}>
                          {aiConfig.exclude_keywords.slice(0, 10).map((kw, idx) => (
                            <Tag key={idx} color="red" style={{ fontSize: 11 }}>{kw}</Tag>
                          ))}
                          {aiConfig.exclude_keywords.length > 10 && (
                            <Tag style={{ fontSize: 11 }}>+{aiConfig.exclude_keywords.length - 10}</Tag>
                          )}
                        </Space>
                      </div>
                    )}
                  </Space>
                }
                title="Конфигурация AI"
                trigger="click"
              >
                <Button size="small" icon={<InfoCircleOutlined />}>
                  Конфигурация
                </Button>
              </Popover>
              </Tooltip>
            </Space>
          )
        }
      >
        {loadingAIConfig ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Загрузка AI конфигурации...</Text>
            </div>
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Queue position info */}
            {analyzing && queueInfo && (
              <Alert
                message={
                  <Space>
                    <span>Ваш анализ в очереди — позиция {queueInfo.position}</span>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4}>
                    {queueInfo.running_batch && (
                      <Text type="secondary">
                        Сейчас обрабатывается другой batch: {queueInfo.running_batch.processed}/{queueInfo.running_batch.total} URL
                      </Text>
                    )}
                    <Text type="secondary">
                      Впереди ~{queueInfo.urls_ahead} URL. Ориентировочное ожидание: ~{queueInfo.estimated_wait_minutes} мин.
                    </Text>
                  </Space>
                }
                type="warning"
                showIcon
              />
            )}

            {/* Config version mismatch banner */}
            {!analyzing &&
              analyzedCount > 0 &&
              currentBatchConfigVersion != null &&
              currentProjectConfigVersion != null &&
              currentBatchConfigVersion !== currentProjectConfigVersion &&
              onReanalyze && (
                <Alert
                  type="warning"
                  showIcon
                  message="Конфигурация AI была изменена"
                  description={
                    <Space direction="vertical" size={4}>
                      <Text>
                        Текущие результаты получены с версией конфига v{currentBatchConfigVersion}, актуальная — v{currentProjectConfigVersion}.
                        Для применения нового промпта запустите пересчёт.
                      </Text>
                      <Button
                        type="primary"
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={reanalyzing}
                        onClick={() => onReanalyze()}
                        style={{ marginTop: 4 }}
                      >
                        Пересчитать
                      </Button>
                    </Space>
                  }
                />
              )}

            {/* Analysis progress */}
            {analyzing && !queueInfo && (
              <Alert
                message={
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <span>Анализ источников: {analyzedCount} из {totalCount}</span>
                    <Text strong>{Math.round(progressPercent)}%</Text>
                  </Space>
                }
                description={<Progress percent={Math.round(progressPercent)} status="active" showInfo={false} />}
                type="info"
                icon={<LoadingOutlined />}
              />
            )}

            {/* Start analysis */}
            {!analyzing && analyzedCount === 0 && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <ExperimentOutlined style={{ fontSize: 64, color: '#722ed1', marginBottom: 16 }} />
                <Title level={4}>Готово к AI анализу</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                  Искусственный интеллект проанализирует {domains.length} {domains.length === 1 ? 'источник' : domains.length < 5 ? 'источника' : 'источников'} по критериям релевантности
                </Text>

                {/* Prompt Preview */}
                {aiConfig && (
                  <PromptPreviewBlock config={aiConfig} />
                )}

                <Button type="primary" icon={<ExperimentOutlined />} onClick={onStartAnalysis} size="large" style={{ marginTop: 16 }}>
                  Запустить AI анализ
                </Button>
              </div>
            )}

            {/* Analysis results */}
            {domains.length > 0 && (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Statistics cards */}
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={4}>
                    <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                      <Statistic
                        title={<Text style={{ fontSize: 11 }}>Одобрено</Text>}
                        value={approvedCount}
                        valueStyle={{ color: '#52c41a', fontSize: 24, fontWeight: 700 }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={4}>
                    <Card size="small" style={{ background: '#fff1f0', borderColor: '#ffccc7' }}>
                      <Statistic
                        title={<Text style={{ fontSize: 11 }}>Отклонено</Text>}
                        value={rejectedCount}
                        valueStyle={{ color: '#ff4d4f', fontSize: 24, fontWeight: 700 }}
                        prefix={<CloseCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={4}>
                    <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
                      <Statistic
                        title={<Text style={{ fontSize: 11 }}>Проверить</Text>}
                        value={uncertainCount}
                        valueStyle={{ color: '#faad14', fontSize: 24, fontWeight: 700 }}
                        prefix={<QuestionCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={4}>
                    <Card size="small" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
                      <Statistic
                        title={<Text style={{ fontSize: 11 }}>Ожидает</Text>}
                        value={pendingCount}
                        valueStyle={{ color: '#1890ff', fontSize: 24, fontWeight: 700 }}
                        prefix={<LoadingOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card size="small" style={{ background: '#fafafa', borderColor: '#d9d9d9' }}>
                      <Statistic
                        title={<Text style={{ fontSize: 11 }}>Всего источников</Text>}
                        value={domains.length}
                        valueStyle={{ fontSize: 24, fontWeight: 700 }}
                        suffix={<Text type="secondary" style={{ fontSize: 14 }}>({analyzedCount} проанализ.)</Text>}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* Filters and search */}
                <Card size="small">
                  <Row gutter={16} align="middle">
                    <Col flex="auto">
                      <Space>
                        <FilterOutlined style={{ color: '#8c8c8c' }} />
                        <Segmented
                          options={[
                            { label: `Все (${domains.length})`, value: 'all' },
                            {
                              label: (
                                <Space size={4}>
                                  <span>Одобрено</span>
                                  <Badge count={approvedCount} overflowCount={99999} style={{ backgroundColor: '#52c41a' }} />
                                </Space>
                              ),
                              value: 'approved'
                            },
                            {
                              label: (
                                <Space size={4}>
                                  <span>Отклонено</span>
                                  <Badge count={rejectedCount} overflowCount={99999} style={{ backgroundColor: '#ff4d4f' }} />
                                </Space>
                              ),
                              value: 'rejected'
                            },
                            {
                              label: (
                                <Space size={4}>
                                  <span>Проверить</span>
                                  <Badge count={uncertainCount} overflowCount={99999} style={{ backgroundColor: '#faad14' }} />
                                </Space>
                              ),
                              value: 'uncertain'
                            },
                          ]}
                          value={aiFilter}
                          onChange={(value) => onAiFilterChange(value as any)}
                        />
                      </Space>
                    </Col>
                    <Col flex="300px">
                      <Input
                        placeholder="Поиск по доменам..."
                        prefix={<SearchOutlined />}
                        allowClear
                        value={aiSearchText}
                        onChange={(e) => onAiSearchTextChange(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* Sources table */}
                <Card size="small" styles={{ body: { padding: 0 } }}>
                  <Table
                    columns={aiAnalysisColumns}
                    dataSource={filteredDomains}
                    rowKey="host"
                    pagination={{
                      defaultPageSize: 10,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      showTotal: (total) => `Всего: ${total} источников`,
                      size: 'small',
                    }}
                    size="small"
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            aiFilter !== 'all' || aiSearchText
                              ? 'Нет источников, соответствующих фильтрам'
                              : 'Нет проанализированных источников'
                          }
                        />
                      ),
                    }}
                  />
                </Card>
              </Space>
            )}
          </Space>
        )}
      </Card>
    </Space>
  )
}
